import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatInputField } from "@cat-digital-blocks/react/bedrock/components/InputField";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatDropdown } from "@cat-digital-blocks/react/bedrock/components/Dropdown";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatLoadingIndicator } from "@cat-digital-blocks/react/bedrock/components/LoadingIndicator";
import { CatTextareaField } from "@cat-digital-blocks/react/bedrock/components/TextareaField";
import { CatCheckboxField } from "@cat-digital-blocks/react/bedrock/components/CheckboxField";
import {
  useCreateAttributeMappingMutation,
  useGetDataSourceMappingsQuery,
  useLazyGetViewColumnMetadataQuery,
  useGetOperatorsByDataTypeQuery,
} from "@services/dcmApi";
import type {
  AttributeMappingRequest,
  DataSourceMappingResponse,
  ColumnMetadataResponse,
  OperatorResponse,
} from "@/types/api";
import styles from "./AttributeRegistration.module.scss";

// Helper function to map MySQL data types to simplified types
// Returns types that match PAL_RULE_OPERATORS table: Integer, Decimal, String, Date, Boolean
const mapDataType = (mysqlType: string | undefined): string => {
  if (!mysqlType) return "String";
  const upperType = mysqlType.toUpperCase();

  if (upperType.includes("BIGINT") || upperType.includes("INT")) {
    return "Integer";
  }

  if (
    upperType.includes("DECIMAL") ||
    upperType.includes("FLOAT") ||
    upperType.includes("DOUBLE") ||
    upperType.includes("NUMERIC")
  ) {
    return "Decimal";
  }

  if (upperType.includes("DATETIME") || upperType.includes("TIMESTAMP")) {
    return "Date";
  }

  if (upperType.includes("DATE")) {
    return "Date";
  }

  if (upperType.includes("BOOL") || upperType === "TINYINT(1)") {
    return "Boolean";
  }

  return "String";
};

interface CreateAttributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  palType: string;
  userName?: string;
}

const CreateAttributeModal = ({
  isOpen,
  onClose,
  onSuccess,
  palType,
  userName,
}: CreateAttributeModalProps) => {
  const { t } = useTranslation();

  const [selectedDataSourceMappingId, setSelectedDataSourceMappingId] =
    useState<number | null>(null);
  const [mappingColumn, setMappingColumn] = useState("");
  const [description, setDescription] = useState("");
  const [dataType, setDataType] = useState("");
  const [allowedOperators, setAllowedOperators] = useState<string[]>([]);
  const [allowedValues, setAllowedValues] = useState<string[]>([]);
  const [newValue, setNewValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Fetch data source mappings for this PAL type
  const { data: dataSourceMappings = [], isLoading: isLoadingMappings } =
    useGetDataSourceMappingsQuery(
      { palType },
      {
        skip: !isOpen,
      },
    );

  // Lazy query for column metadata - triggered when data source is selected
  const [
    fetchColumnMetadata,
    { data: columnMetadata = [], isLoading: isLoadingColumns },
  ] = useLazyGetViewColumnMetadataQuery();

  // Fetch operators by data type
  const { data: availableOperators = [] } = useGetOperatorsByDataTypeQuery(
    { dataType: dataType || undefined },
    { skip: !dataType },
  );

  const [createAttribute, { isLoading }] = useCreateAttributeMappingMutation();

  const sortedMappings = useMemo(() => {
    return [...dataSourceMappings].sort((a, b) =>
      a.sourceName.localeCompare(b.sourceName),
    );
  }, [dataSourceMappings]);

  // Sort column metadata by ordinal position
  const sortedColumns = useMemo(() => {
    return [...columnMetadata].sort(
      (a, b) => (a.ordinalPosition || 0) - (b.ordinalPosition || 0),
    );
  }, [columnMetadata]);

  const handleDataSourceMappingChange = useCallback(
    (event: CustomEvent) => {
      const selectedValue = event.detail?.value;
      const val =
        typeof selectedValue === "string"
          ? selectedValue
          : selectedValue?.value;
      if (val) {
        const mappingId = parseInt(val, 10);
        setSelectedDataSourceMappingId(mappingId);
        setError(null);

        // Find the selected mapping
        const mapping = dataSourceMappings.find((m) => m.id === mappingId);
        if (mapping) {
          // Reset column-related fields
          setMappingColumn("");
          setDescription("");
          setDataType("");
          setAllowedOperators([]);
          setAllowedValues([]);

          // Fetch column metadata for this view
          if (mapping.schemaName) {
            fetchColumnMetadata({
              schemaName: mapping.schemaName,
              viewName: mapping.sourceName,
            });
          }
        }
      }
    },
    [dataSourceMappings, fetchColumnMetadata],
  );

  // Handle column selection
  const handleMappingColumnChange = useCallback(
    (event: CustomEvent) => {
      const selectedValue = event.detail?.value;
      const val =
        typeof selectedValue === "string"
          ? selectedValue
          : selectedValue?.value;
      if (val) {
        setMappingColumn(val);

        // Find selected column metadata
        const column = columnMetadata.find((c) => c.columnName === val);
        if (column) {
          // Auto-populate dataType from column metadata
          const mappedType = mapDataType(column.dataType);
          setDataType(mappedType);
          // Auto-populate description from column comment
          setDescription(column.description || "");
          // Auto-populate allowedValues if present in comment (parse comma-separated string)
          if (column.allowedValues && column.allowedValues.trim().length > 0) {
            const valuesArray = column.allowedValues
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0);
            setAllowedValues(valuesArray);
          } else {
            setAllowedValues([]);
          }
          // Reset operators when column changes
          setAllowedOperators([]);
        }
      }
    },
    [columnMetadata],
  );

  // Handle operator checkbox toggle
  const handleOperatorToggle = useCallback(
    (operator: string, checked: boolean) => {
      setError(null); // Clear any validation error
      if (checked) {
        setAllowedOperators((prev) => [...prev, operator]);
      } else {
        setAllowedOperators((prev) => prev.filter((op) => op !== operator));
      }
    },
    [],
  );

  const handleDescriptionChange = useCallback((event: CustomEvent) => {
    setDescription(event.detail?.value ?? "");
  }, []);

  const handleAddValue = useCallback(() => {
    if (newValue.trim() && !allowedValues.includes(newValue.trim())) {
      setAllowedValues([...allowedValues, newValue.trim()]);
      setNewValue("");
    }
  }, [newValue, allowedValues]);

  const handleRemoveValue = useCallback((value: string) => {
    setAllowedValues((prev) => prev.filter((v) => v !== value));
  }, []);

  const validateForm = useCallback((): boolean => {
    if (!selectedDataSourceMappingId) {
      setError(t("admin.attributeRegistration.validation.dataSourceRequired"));
      return false;
    }
    if (!mappingColumn.trim()) {
      setError(
        t("admin.attributeRegistration.validation.mappingColumnRequired"),
      );
      return false;
    }
    if (!dataType.trim()) {
      setError(t("admin.attributeRegistration.validation.dataTypeRequired"));
      return false;
    }
    if (!description.trim()) {
      setError(t("admin.attributeRegistration.validation.descriptionRequired"));
      return false;
    }
    if (allowedOperators.length === 0) {
      setError(t("admin.attributeRegistration.validation.operatorsRequired"));
      return false;
    }
    return true;
  }, [
    selectedDataSourceMappingId,
    mappingColumn,
    dataType,
    description,
    allowedOperators,
    t,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm() || !selectedDataSourceMappingId) return;
    try {
      const request: AttributeMappingRequest = {
        mappingColumn: mappingColumn.trim() || undefined,
        description: description.trim() || undefined,
        dataType: dataType || undefined,
        allowedOperators:
          allowedOperators.length > 0 ? allowedOperators : undefined,
        allowedValues: allowedValues.length > 0 ? allowedValues : undefined,
      };
      await createAttribute({
        mappingId: selectedDataSourceMappingId,
        body: request,
        userName,
      }).unwrap();
      onSuccess();
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        err.status === 409
      ) {
        setError(t("admin.attributeRegistration.error.duplicate"));
      } else {
        setError(t("admin.attributeRegistration.error.createFailed"));
      }
    }
  }, [
    validateForm,
    selectedDataSourceMappingId,
    mappingColumn,
    description,
    dataType,
    allowedOperators,
    allowedValues,
    createAttribute,
    userName,
    onSuccess,
    t,
  ]);

  const handleClose = useCallback(() => {
    setSelectedDataSourceMappingId(null);
    setMappingColumn("");
    setDescription("");
    setDataType("");
    setAllowedOperators([]);
    setAllowedValues([]);
    setNewValue("");
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("admin.attributeRegistration.createModal.title")}
          </CatHeading>
        </div>
        <div className={styles.modalBody}>
          {error && (
            <CatInlineNotification
              variant="error"
              className={styles.errorMessage}
            >
              {error}
            </CatInlineNotification>
          )}

          {/* Data Source Selection */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.dataSource")} *
            </label>
            {isLoadingMappings ? (
              <div className={styles.loadingContainer}>
                <CatLoadingIndicator small />
                <span>{t("common.loading")}</span>
              </div>
            ) : dataSourceMappings.length === 0 ? (
              <CatInlineNotification variant="warning">
                {t("admin.attributeRegistration.noDataSources")}
              </CatInlineNotification>
            ) : (
              <CatDropdown
                label=""
                size="sm"
                value={selectedDataSourceMappingId?.toString() || ""}
                onBlSelect={handleDataSourceMappingChange}
                disabled={isLoading}
                placeholder={t(
                  "admin.attributeRegistration.createModal.dataSourcePlaceholder",
                )}
              >
                <CatList spacing="padded">
                  {sortedMappings.map((ds: DataSourceMappingResponse) => (
                    <CatListItem key={ds.id} value={ds.id.toString()}>
                      {ds.sourceName}
                    </CatListItem>
                  ))}
                </CatList>
              </CatDropdown>
            )}
          </div>

          {/* Mapping Column */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.mappingColumn")} *
            </label>
            {isLoadingColumns ? (
              <div className={styles.loadingContainer}>
                <CatLoadingIndicator small />
                <span>{t("common.loading")}</span>
              </div>
            ) : !selectedDataSourceMappingId ? (
              <CatDropdown
                label=""
                size="sm"
                disabled={true}
                placeholder={t(
                  "admin.attributeRegistration.createModal.selectDataSourceFirst",
                )}
              >
                <CatList spacing="padded">
                  <CatListItem value="">-</CatListItem>
                </CatList>
              </CatDropdown>
            ) : (
              <CatDropdown
                label=""
                size="sm"
                value={mappingColumn}
                onBlSelect={handleMappingColumnChange}
                disabled={isLoading}
                placeholder={t(
                  "admin.attributeRegistration.createModal.mappingColumnPlaceholder",
                )}
              >
                <CatList spacing="padded">
                  {sortedColumns.map((col: ColumnMetadataResponse) => (
                    <CatListItem key={col.columnName} value={col.columnName}>
                      {col.columnName}
                    </CatListItem>
                  ))}
                </CatList>
              </CatDropdown>
            )}
          </div>

          {/* Data Type */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.dataType")} *
            </label>
            <CatInputField
              label=""
              value={dataType}
              placeholder={t(
                "admin.attributeRegistration.createModal.dataTypePlaceholder",
              )}
              disabled
            />
          </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.description")} *
            </label>
            <div className={styles.noLabelField}>
              <CatTextareaField
                label=" "
                value={description}
                onBlChange={handleDescriptionChange}
                placeholder={t(
                  "admin.attributeRegistration.createModal.descriptionPlaceholder",
                )}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {/* Allowed Operators (checkboxes from PAL_Rule_operators) */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.allowedOperators")} *
            </label>
            {!dataType ? (
              <div className={styles.fieldHint}>
                {t(
                  "admin.attributeRegistration.createModal.selectColumnForOperators",
                )}
              </div>
            ) : availableOperators.length === 0 ? (
              <div className={styles.fieldHint}>
                {t(
                  "admin.attributeRegistration.createModal.noOperatorsForType",
                )}
              </div>
            ) : (
              <div className={styles.operatorCheckboxes}>
                {availableOperators.map((opResponse: OperatorResponse) => {
                  const op = opResponse.operator || "";
                  if (!op) return null;
                  const isChecked = allowedOperators.includes(op);
                  return (
                    <CatCheckboxField
                      key={op}
                      value={op}
                      checked={isChecked}
                      onBlCheckboxChange={(e: CustomEvent) => {
                        const checked = e.detail?.checked ?? false;
                        handleOperatorToggle(op, checked);
                      }}
                      disabled={isLoading}
                      hasHover
                    >
                      {op}
                    </CatCheckboxField>
                  );
                })}
              </div>
            )}
          </div>

          {/* Allowed Values */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.attributeRegistration.createModal.allowedValues")}
            </label>
            <div className={styles.tagInput}>
              <CatInputField
                label=" "
                value={newValue}
                onBlChange={(e: CustomEvent) =>
                  setNewValue(e.detail?.value ?? "")
                }
                placeholder={t(
                  "admin.attributeRegistration.createModal.valuePlaceholder",
                )}
                disabled={isLoading}
              />
              <CatButton
                variant="ghost"
                size="sm"
                onClick={handleAddValue}
                disabled={isLoading || !newValue.trim()}
              >
                {t("common.add")}
              </CatButton>
            </div>
            {allowedValues.length > 0 && (
              <div className={styles.tagContainer}>
                {allowedValues.map((val) => (
                  <span key={val} className={styles.tag}>
                    {val}
                    <span
                      className={styles.tagRemove}
                      onClick={() => handleRemoveValue(val)}
                    >
                      ×
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.modalActions}>
          <CatButton
            variant="contrast"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || dataSourceMappings.length === 0}
          >
            {isLoading ? t("common.saving") : t("common.save")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

export default CreateAttributeModal;
